import BaseSkeleton from 'components/Generic/BaseSkeleton';
import { useI18n } from 'next-localization';
import { useMemo } from 'react';
import useHelpers from 'hooks/helpers';
import { standardFormat } from '@/constants/dateFormats';
import { PriceAggregates } from 'model/ApiGateway';

type Props = {
  data: PriceAggregates;
  isFetching: boolean;
  className: string;
};

const FundDetailsPremiumDiscountBarChartHistoricalDataTable = ({
  data,
  isFetching,
  className,
}: Props) => {
  const { t } = useI18n();
  const { localizeDate, formatNumber } = useHelpers();

  const tableData = useMemo(() => {
    if (!data) return;

    const {
      dateOfGreatestPremium,
      dateOfGreatestDiscount,
      greatestPremium,
      greatestDiscount,
      numberOfPremiumDays,
      navNumberOfDays,
      numberOfDiscountDays,
      totalNumberOfDays,
      numberOfDaysBetween,
      percentageOfDaysBetween,
    } = data;
    const greatestPremiumTitle = `${t(
      'fundDetailsPremiumDiscountGreatestPremium'
    )} - ${localizeDate(dateOfGreatestPremium, standardFormat)}`;

    const greatestDiscountTitle = `${t(
      'fundDetailsPremiumDiscountGreatestDiscount'
    )} - ${localizeDate(dateOfGreatestDiscount, standardFormat)}`;

    return [
      {
        title: greatestPremiumTitle,
        value: formatNumber(greatestPremium),
        key: 'dateOfGreatestPremium',
      },
      {
        title: greatestDiscountTitle,
        value: formatNumber(greatestDiscount),
        key: 'dateOfGreatestDiscount',
      },
      {
        title: t('fundDetailsPremiumDiscountChartPremium'),
        value: numberOfPremiumDays,
        key: 'numberOfPremiumDays',
      },
      {
        title: t('fundDetailsPremiumDiscountNav'),
        value: navNumberOfDays,
        key: 'navNumberOfDays',
      },
      {
        title: t('fundDetailsPremiumDiscountDiscount'),
        value: numberOfDiscountDays,
        key: 'numberOfDiscountDays',
      },
      {
        title: t('total'),
        value: totalNumberOfDays,
        key: 'totalNumberOfDays',
      },
      {
        title: t('fundDetailsPremiumDiscountNumOfDaysBetween'),
        value: numberOfDaysBetween,
        key: 'numberOfDaysBetween',
      },
      {
        title: t('fundDetailsPremiumDiscountPercOfDaysbetween'),
        value: formatNumber(percentageOfDaysBetween),
        key: 'percentageOfDaysBetween',
      },
    ].filter((item) => item.key in data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div className={className}>
      {isFetching ? (
        <BaseSkeleton height="120px" />
      ) : tableData ? (
        <div className="abrdn-table">
          <table
            className="minimal"
            tabIndex="0"
            aria-label={t('fundDetailsPremiumDiscountHistoricalTitle')}
          >
            <thead hidden>
              <tr>
                <th scope="col">Premium and Discount historical data column name</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((tr) => (
                <tr key={tr[0]}>
                  <td>{tr.title || t('notAvailableShortMessage')}</td>
                  <td>{tr.value !== null ? tr.value : t('notAvailableShortMessage')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="body-default">{t('noResultsMessage')}</div>
      )}
    </div>
  );
};

export default FundDetailsPremiumDiscountBarChartHistoricalDataTable;
